import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import FlagIcon from "@mui/icons-material/Flag";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { Flag } from "../types";
import { useTheme } from "@mui/material/styles";
import { useDataGrid } from "@refinedev/mui";
import { useTranslation } from "../../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
  open: boolean;
  orderId: number;
  onClose: () => void;
};

export const AssignFlag = ({ open, orderId, onClose }: Props) => {
  const theme = useTheme();
  const { translate, translateWithoutPrefix } =
    useTranslation("pages.orders.flag");
  const invalidate = useInvalidate();
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const queryClient = useQueryClient();

  const { dataGridProps } = useDataGrid<Flag>({
    resource: "store/user-flags",
  });

  const { mutate } = useCustomMutation();

  const onSubmit = (params: GridRowParams<Flag>) => {
    mutate(
      {
        url: "store/order-flags",
        method: "post",
        values: {
          userFlagId: params.row.id,
          orderId,
        },
        successNotification: () => ({
          message: translate("successAssignFlag"),
          type: "success",
        }),
      },
      {
        onSuccess: async () => {
          const queryKey = [
            "order-detail",
            selectedCountry?.id,
            String(orderId),
          ];

          invalidate({
            resource: "all",
            invalidates: ["all"],
          });

          await queryClient.invalidateQueries({ queryKey });
          await queryClient.refetchQueries({ queryKey });
          onClose();
        },
      },
    );
  };

  const columns: GridColDef<Flag>[] = [
    { field: "flagName", headerName: translate("flagTitle"), flex: 1 },
    {
      field: "flagColor",
      headerName: translate("flagColor"),
      flex: 1,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
        >
          <FlagIcon fontSize="large" sx={{ color: params.value }} />
        </Box>
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{translate("selectFlag")}</DialogTitle>
      <DialogContent>
        <Box>
          <DataGrid
            {...dataGridProps}
            columns={columns}
            onRowClick={onSubmit}
            disableMultipleRowSelection
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: "pointer",
                backgroundColor: theme.palette.primary.light,
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {translateWithoutPrefix("buttons.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
